import type { CoachConfig } from "../engine/coach-params";
import { el } from "./dom";

// Drawer slider metadata for the numeric Coach params — the practical tuning
// knobs, kept as data separate from the controls wiring. `warnBelow`/`warnText`
// render a caution under a slider while its value is below the threshold.

interface CoachNumParam {
  key: keyof CoachConfig;
  label: string;
  min: number;
  max: number;
  step: number;
  warnBelow?: number;
  warnText?: string;
}

const COACH_NUM_PARAMS: CoachNumParam[] = [
  { key: "tempoFloor", label: "Tempo floor", min: 0.3, max: 0.9, step: 0.05 },
  { key: "tempoStep", label: "Tempo step", min: 0.02, max: 0.2, step: 0.01 },
  { key: "abortDensity", label: "Abort density (run-through)", min: 0.1, max: 0.6, step: 0.05 },
  { key: "rampTolerance", label: "Ramp tolerance (mistakes)", min: 0, max: 5, step: 1 },
  { key: "loopMinBars", label: "Min drill bars", min: 1, max: 8, step: 1 },
  { key: "masteryReps", label: "Mastery reps", min: 1, max: 4, step: 1 },
  { key: "expandBars", label: "Expand bars", min: 1, max: 8, step: 1 },
  {
    key: "masteryCompletenessPct",
    label: "Gate: completeness %",
    min: 0,
    max: 100,
    step: 5,
    warnBelow: 100,
    warnText:
      "⚠ Below 100%, a bar can “master” with a dropped note — you won’t be made to play every note.",
  },
  { key: "masteryCorrectnessPct", label: "Gate: correctness %", min: 0, max: 100, step: 5 },
  { key: "masteryTimingPct", label: "Gate: timing %", min: 0, max: 100, step: 5 },
  {
    key: "masteryTimingFloorPct",
    label: "Gate: timing floor % (beginner)",
    min: 0,
    max: 100,
    step: 5,
  },
  {
    key: "masteryTimingFullProf",
    label: "Gate: timing full @ proficiency",
    min: 1,
    max: 4,
    step: 0.25,
  },
  { key: "masteryTimingMs", label: "Gate: timing ms", min: 20, max: 120, step: 10 },
  { key: "pSwitchChain", label: "Chain rotation", min: 0, max: 1, step: 0.1 },
  { key: "staleDays", label: "Stale days", min: 0, max: 30, step: 1 },
  { key: "decayReps", label: "Decay reps", min: 0, max: 3, step: 1 },
];

/** A built numeric-param slider: its param, the live DOM refs, and a warn updater. */
interface CoachNumField {
  p: CoachNumParam;
  input: HTMLInputElement;
  label: HTMLSpanElement;
  warn: HTMLParagraphElement | null;
  updateWarn(value: number): void;
}

/** Build the Coach numeric sliders (range + value label + optional warning),
 *  each emitting its patch via `onConfig`. The drawer lays out + seeds them. */
export function buildCoachNumFields(
  onConfig: (patch: Partial<CoachConfig>) => void,
): CoachNumField[] {
  return COACH_NUM_PARAMS.map((p) => {
    const input = el<HTMLInputElement>("input", {
      type: "range",
      min: String(p.min),
      max: String(p.max),
      step: String(p.step),
    });
    const label = el<HTMLSpanElement>("span", { textContent: "" });
    const warn = p.warnText
      ? el<HTMLParagraphElement>("p", { class: "coach-warn", textContent: p.warnText })
      : null;
    const updateWarn = (value: number) => {
      if (warn && p.warnBelow !== undefined) {
        warn.style.display = value < p.warnBelow ? "" : "none";
      }
    };
    input.oninput = () => {
      label.textContent = input.value;
      updateWarn(Number(input.value));
      onConfig({ [p.key]: Number(input.value) } as Partial<CoachConfig>);
    };
    return { p, input, label, warn, updateWarn };
  });
}
